import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, ArrowRight, Loader2, Instagram, Facebook, Music2 } from "lucide-react";
import { Reveal, ChapterHeading } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Group Fitness",
  "Fitness Coaching",
  "Nutrition Coaching",
  "Fitness & Nutrition Coaching",
  "Not sure yet",
];

const SOCIALS = [
  { icon: Instagram, platform: "Instagram", handle: "@me1stfitness", url: "https://www.instagram.com/me1stfitness" },
  { icon: Music2, platform: "TikTok", handle: "@Fitnessmom43", url: "https://www.tiktok.com/@Fitnessmom43" },
  { icon: Facebook, platform: "Facebook", handle: "Me 1st Fitness", url: "https://www.facebook.com/search/top?q=me%201st%20fitness%20red%20deer" },
];

const initial = { name: "", email: "", phone: "", interest: "", goals: "", message: "" };

const fieldClass =
  "w-full bg-transparent border-b-2 border-sand/20 focus:border-brand outline-none py-3 text-sand placeholder:text-sand/35 transition-colors duration-300";

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong — please try again or call (403) 505-5078.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-mint" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <ChapterHeading number="08" label="Get Started" title="Ready to Put Yourself First?" />
          <Reveal delay={0.1}>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-charcoal/70 max-w-md">
              Take the first step toward a healthier, stronger, more confident you. Send a message
              and we'll find the right fit together.
            </p>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="contact-get-started-btn"
              className="group mt-8 rounded-full bg-charcoal text-sand font-bold px-8 py-4 text-sm tracking-wide inline-flex items-center gap-2 hover:bg-brand-dark transition-colors duration-300"
            >
              GET STARTED
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3 text-charcoal/75" data-testid="contact-location">
                <span className="w-10 h-10 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-brand-dark" />
                </span>
                <span className="font-medium">Red Deer, Alberta, Canada</span>
              </div>
              <a
                href="tel:+14035055078"
                className="flex items-center gap-3 text-charcoal/75 hover:text-charcoal transition-colors duration-200"
                data-testid="contact-phone-link"
              >
                <span className="w-10 h-10 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-brand-dark" />
                </span>
                <span className="font-medium">(403) 505-5078</span>
              </a>

              <div className="pt-4" data-testid="contact-socials">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-charcoal/45 mb-3">
                  Follow along
                </p>
                <div className="flex flex-col gap-3">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.platform}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${s.platform.toLowerCase()}`}
                      className="group flex items-center gap-3 text-charcoal/75 hover:text-charcoal transition-colors duration-200"
                    >
                      <span className="w-10 h-10 rounded-full bg-charcoal flex items-center justify-center shrink-0 group-hover:bg-brand transition-colors duration-300">
                        <s.icon size={17} className="text-sand group-hover:text-charcoal transition-colors duration-300" />
                      </span>
                      <span className="font-medium">
                        {s.handle}
                        <span className="block text-xs text-charcoal/45">{s.platform}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="rounded-3xl bg-charcoal border border-sand/10 shadow-[0_20px_50px_rgb(0,0,0,0.15)] p-8 sm:p-10"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label htmlFor="contact-name" className="text-xs tracking-[0.15em] uppercase font-bold text-sand/50">
                  Name *
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs tracking-[0.15em] uppercase font-bold text-sand/50">
                  Email *
                </label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@email.com"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="text-xs tracking-[0.15em] uppercase font-bold text-sand/50">
                  Phone
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(403) 555-0123"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="contact-interest" className="text-xs tracking-[0.15em] uppercase font-bold text-sand/50">
                  What are you interested in?
                </label>
                <select
                  id="contact-interest"
                  data-testid="contact-interest-select"
                  value={form.interest}
                  onChange={set("interest")}
                  className={`${fieldClass} cursor-pointer`}
                >
                  <option value="">Choose one…</option>
                  {INTERESTS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-goals" className="text-xs tracking-[0.15em] uppercase font-bold text-sand/50">
                  Fitness goals
                </label>
                <input
                  id="contact-goals"
                  data-testid="contact-goals-input"
                  value={form.goals}
                  onChange={set("goals")}
                  placeholder="e.g. Build strength, move without pain, feel more confident"
                  className={fieldClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="text-xs tracking-[0.15em] uppercase font-bold text-sand/50">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  rows={4}
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell me a little about yourself…"
                  className={`${fieldClass} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-send-btn"
              className="mt-9 rounded-full bg-brand text-charcoal font-bold px-10 py-4 text-sm tracking-wide inline-flex items-center gap-2 hover:bg-sand transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? (
                <>
                  SENDING <Loader2 size={18} className="animate-spin" />
                </>
              ) : (
                <>
                  SEND MESSAGE <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
